<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import PilotKnowledgeBasesAPI from 'dashboard/api/pilot/knowledgeBases';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// 导入 SVG 图片
import documentPopoverLight from 'assets/images/dashboard/pilot/document-popover-light.svg';
import documentPopoverDark from 'assets/images/dashboard/pilot/document-popover-dark.svg';
import faqsPopoverLight from 'assets/images/dashboard/pilot/faqs-popover-light.svg';
import faqsPopoverDark from 'assets/images/dashboard/pilot/faqs-popover-dark.svg';

const props = defineProps({
  actionType: {
    type: String,
    default: 'uploadDocument',
  },
  // 当前已关联的知识库（用于 crawlWechat 场景显示"手动指定到此知识库"）
  currentKnowledgeBase: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'select', 'create-kb']);

const { t } = useI18n();

// ========== 状态 ==========
const dialogRef = ref(null);
const searchQuery = ref('');
const imageError = ref(false);
const listContainerRef = ref(null);
const knowledgeBases = ref([]);
const totalCount = ref(0);
const currentPage = ref(1);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const selectedId = ref(null);

const PAGE_SIZE = 10;

// ========== 场景配置 ==========
const sceneConfigs = {
  uploadDocument: {
    color: '#14A2B5',
    bgColor: 'bg-[#14A2B5]/10',
    icon: 'i-ri-file-word-2-line',
    popoverImageLight: documentPopoverLight,
    popoverImageDark: documentPopoverDark,
    tip: '文档是以PDF、Word、TXT等格式存储的离线知识载体。将这些文档导入系统，由平台进行解析处理后构建为业务知识库，让AI学习并使用其中的内容进行智能回答。\n\n如何运作？\n\n1. 选择一个要导入内容的知识库。\n\n2. 上传文档：支持导入PDF、Word、TXT、Markdown等多种格式文件，可单份或批量上传。\n\n3. 自动解析并语义化：系统对文档内容进行识别、提取和格式化处理，并切片和语义索引，无缝接入您的业务知识库。\n\n4. AI智能问答：您的AI智能体在接入该知识库后，即可基于文档内容，为用户提供精准的问答与信息检索服务，实现离线知识的智能化利用。',
    footerTip: '← 左侧选择一个知识库，下一步对这个知识库上传本地文档。',
  },
  crawlWechat: {
    color: '#2DD4BF',
    bgColor: 'bg-[#2DD4BF]/10',
    icon: 'i-ri-wechat-2-line',
    popoverImageLight: documentPopoverLight,
    popoverImageDark: documentPopoverDark,
    tip: '公众号文章内容是存储在微信公众号中的优质内容，可以导入知识库，让 AI 学习并使用这些知识进行智能回答。\n\n如何运作？\n\n1. 选择一个要导入内容的知识库。\n\n2. 智能抓取：系统自动爬取该公众号。\n\n3. 自动解析入库：对抓取到的文章内容进行去重、格式化处理和语义解析，无缝接入您的业务知识库。\n\n4. AI智能问答：您的AI智能体在接入该知识库后，即可基于这些公众号内容，为用户提供精准的问答与信息检索服务。',
    footerTip: '← 左侧选择一个知识库，下一步开始抓取公众号。',
  },
  crawlWebpage: {
    color: '#FE6206',
    bgColor: 'bg-[#FE6206]/10',
    icon: 'i-lucide-globe',
    popoverImageLight: documentPopoverLight,
    popoverImageDark: documentPopoverDark,
    tip: '网页是公开互联网上的信息载体，可以将指定网页的内容导入知识库，让AI学习并使用这些知识进行智能回答。\n\n如何运作？\n\n1. 选择一个要导入内容的知识库。\n\n2. 智能抓取：填写您想要抓取的网页URL，完成简单配置；系统自动抓取该网页的内容。\n\n3. 自动解析入库：对抓取到的内容进行去重、格式清洗和语义解析，无缝接入您的业务知识库。\n\n4. AI智能问答：您的AI智能体在接入该知识库后，即可基于这些网页内容，为用户提供精准的问答与信息检索服务。',
    footerTip: '← 左侧选择一个知识库，下一步开始抓取网页内容。',
  },
  addFaq: {
    color: '#992AFF',
    bgColor: 'bg-[#992AFF]/10',
    icon: 'i-lucide-message-circle-question',
    popoverImageLight: faqsPopoverLight,
    popoverImageDark: faqsPopoverDark,
    tip: 'FAQ（常见问题与解答）是经过梳理的标准问答对，可直接导入知识库，让AI学习并使用这些知识进行智能回答。\n\n如何运作？\n\n1. 选择一个要导入内容的知识库。\n\n2. 导入FAQ：手动录入或批量上传问答对，支持Excel/CSV等格式。\n\n3. 自动建库：系统对问答对进行结构化处理和语义索引，接入业务知识库。\n\n4. AI智能问答：AI智能体接入后，即可基于标准FAQ，为用户提供精准、一致的智能问答服务。',
    footerTip: '← 左侧选择一个知识库，下一步开始添加FAQs。',
  },
  switch: {
    color: '#6B7280',
    bgColor: 'bg-[#6B7280]/10',
    icon: 'i-lucide-arrow-right-left',
    popoverImageLight: documentPopoverLight,
    popoverImageDark: documentPopoverDark,
    tip: '切换到其他知识库继续操作。选择一个新的知识库后，当前页面将更新为该知识库。',
    footerTip: '← 左侧选择一个知识库进行切换。',
  },
};

// ========== 计算属性 ==========
const currentScene = computed(
  () => sceneConfigs[props.actionType] || sceneConfigs.uploadDocument
);

// 当前显示的知识库（选中则显示选中的，否则显示传入的）
const displayKnowledgeBase = computed(() => {
  if (selectedId.value) {
    return knowledgeBases.value.find(kb => kb.id === selectedId.value);
  }
  return props.currentKnowledgeBase;
});

const hasMore = computed(() => knowledgeBases.value.length < totalCount.value);

const dialogTitle = computed(() => {
  const titleKeys = {
    uploadDocument: 'TITLE_UPLOAD',
    crawlWechat: 'TITLE_WECHAT',
    crawlWebpage: 'TITLE_WEBPAGE',
    addFaq: 'TITLE_FAQ',
    importFaq: 'TITLE_IMPORT_FAQ',
    switch: 'TITLE_SWITCH',
  };
  return t(
    `PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.${titleKeys[props.actionType] || titleKeys.uploadDocument}`
  );
});

const confirmLabel = computed(() =>
  t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.CONFIRM')
);

// ========== 方法 ==========
const handleImageError = () => {
  imageError.value = true;
};

const fetchKnowledgeBases = async (page = 1, append = false) => {
  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }

  try {
    const params = { page, per_page: PAGE_SIZE, search: searchQuery.value };
    const { data } = await PilotKnowledgeBasesAPI.get(params);

    knowledgeBases.value = append
      ? [...knowledgeBases.value, ...data.payload]
      : data.payload;
    totalCount.value =
      data.meta?.total_count || data.meta?.count || data.meta?.total || 0;
    currentPage.value = page;
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

const handleScroll = event => {
  const container = event.target;
  const scrollBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight;

  if (scrollBottom < 50 && hasMore.value && !isLoadingMore.value) {
    loadMore();
  }
};

const loadMore = () => {
  if (hasMore.value && !isLoadingMore.value) {
    fetchKnowledgeBases(currentPage.value + 1, true);
  }
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const handleSelect = kb => {
  selectedId.value = kb.id;
};

const handleConfirm = () => {
  // 优先使用选中的知识库，否则使用显示的知识库（手动指定场景）
  const targetId = selectedId.value || displayKnowledgeBase.value?.id;
  if (targetId) {
    emit('select', targetId, props.actionType);
  }
  dialogRef.value?.close();
};

const handleCreateKb = () => {
  emit('create-kb');
  dialogRef.value?.close();
};

const open = () => {
  searchQuery.value = '';
  selectedId.value = null;
  imageError.value = false;
  knowledgeBases.value = [];
  totalCount.value = 0;
  currentPage.value = 1;
  dialogRef.value?.open();
  fetchKnowledgeBases(1, false);
};

const resetAndFetch = () => {
  knowledgeBases.value = [];
  totalCount.value = 0;
  currentPage.value = 1;
  fetchKnowledgeBases(1, false);
  nextTick(() => {
    listContainerRef.value?.scrollTo(0, 0);
  });
};

const debouncedSearch = useDebounceFn(resetAndFetch, 300);

watch(searchQuery, debouncedSearch);

defineExpose({
  dialogRef,
  open,
  close: () => emit('close'),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="3xl"
    overflow-y-auto
    custom-class="!max-w-[960px]"
    @close="emit('close')"
  >
    <div class="flex flex-col h-[680px]">
      <div class="flex flex-1 min-h-0">
        <!-- 左侧：知识库列表 -->
        <div
          class="w-full lg:w-1/2 flex flex-col min-w-0 bg-n-slate-2 dark:bg-n-slate-800/30 rounded-xl p-4"
        >
          <!-- 搜索框 -->
          <div class="pb-2">
            <Input
              v-model="searchQuery"
              type="search"
              size="sm"
              :placeholder="
                t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.SEARCH_PLACEHOLDER')
              "
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-white dark:bg-n-slate-900 ltr:!pl-9 !py-2 rtl:!pr-8',
              ]"
              class="w-full"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2.5 rtl:right-2.5"
                />
              </template>
            </Input>
          </div>

          <!-- 知识库列表 -->
          <div
            ref="listContainerRef"
            class="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto"
            @scroll="handleScroll"
          >
            <!-- 加载中 -->
            <div v-if="isLoading" class="flex justify-center py-8">
              <Spinner :size="20" />
            </div>

            <!-- 空状态 -->
            <div
              v-else-if="knowledgeBases.length === 0"
              class="flex flex-col items-center justify-center py-12 px-4"
            >
              <!-- 搜索无结果 -->
              <template v-if="searchQuery">
                <div
                  class="w-16 h-16 rounded-full bg-n-slate-3 dark:bg-n-slate-800 flex items-center justify-center mb-4"
                >
                  <Icon
                    icon="i-lucide-search-x"
                    class="size-8 text-n-slate-9"
                  />
                </div>
                <p class="text-sm font-medium text-n-slate-11 mb-2">
                  {{ t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.EMPTY_SEARCH') }}
                </p>
                <p class="text-xs text-n-slate-9 mb-4">
                  尝试其他关键词或创建新知识库
                </p>
                <Button
                  :label="t('PILOT.KNOWLEDGE_BASES.ADD_NEW')"
                  icon="i-lucide-plus"
                  size="sm"
                  @click="handleCreateKb"
                />
              </template>

              <!-- 无知识库 -->
              <template v-else>
                <div
                  class="w-16 h-16 rounded-full bg-n-brand/10 flex items-center justify-center mb-4"
                >
                  <Icon icon="i-lucide-book-open" class="size-8 text-n-brand" />
                </div>
                <p class="text-lg font-semibold text-n-slate-12 mb-2">
                  还没有知识库
                </p>
                <p
                  class="text-sm text-n-slate-11 text-center mb-6 max-w-[240px]"
                >
                  创建知识库来组织和管理您的文档，让助手能够更好地使用这些知识。
                </p>
                <Button
                  :label="t('PILOT.KNOWLEDGE_BASES.ADD_NEW')"
                  icon="i-lucide-plus"
                  size="sm"
                  @click="handleCreateKb"
                />
              </template>
            </div>

            <!-- 知识库列表 -->
            <template v-else>
              <button
                v-for="kb in knowledgeBases"
                :key="kb.id"
                type="button"
                class="flex items-center gap-3 p-3 rounded-xl border transition-colors text-left w-full bg-white dark:bg-n-slate-900"
                :class="[
                  selectedId === kb.id
                    ? 'border-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
                    : 'border-n-slate-4 hover:border-n-slate-6 hover:bg-n-slate-1 dark:hover:bg-n-slate-800',
                ]"
                @click="handleSelect(kb)"
              >
                <div
                  class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
                  :class="kb.logo_url ? '' : 'bg-n-brand/10'"
                >
                  <img
                    v-if="kb.logo_url"
                    :src="kb.logo_url"
                    :alt="kb.name"
                    class="w-full h-full object-cover"
                    @error="$event.target.style.display = 'none'"
                  />
                  <span
                    v-if="!kb.logo_url"
                    class="text-sm font-semibold text-n-brand"
                  >
                    {{ kb.name?.charAt(0)?.toUpperCase() || '?' }}
                  </span>
                </div>

                <div class="flex-1 min-w-0">
                  <span
                    class="text-sm font-medium text-n-slate-12 truncate block"
                  >
                    {{ kb.name }}
                  </span>
                  <span class="text-xs text-n-slate-10 truncate block mt-0.5">
                    {{
                      kb.description ||
                      t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION')
                    }}
                  </span>
                </div>

                <Icon
                  v-if="selectedId === kb.id"
                  icon="i-lucide-check"
                  class="size-5 text-n-brand flex-shrink-0"
                />
              </button>

              <div v-if="hasMore" class="flex justify-center py-2">
                <Button
                  :label="t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.LOAD_MORE')"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  :is-loading="isLoadingMore"
                  :disabled="isLoadingMore"
                  @click="fetchKnowledgeBases(currentPage + 1, true)"
                />
              </div>
            </template>
          </div>

          <!-- 底部按钮 -->
          <div class="pt-2">
            <div class="flex items-center justify-end gap-2">
              <Button
                :label="t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.CANCEL')"
                variant="faded"
                color="slate"
                @click="closeDialog"
              />
              <Button
                :label="confirmLabel"
                :disabled="!selectedId && !displayKnowledgeBase"
                @click="handleConfirm"
              />
            </div>
          </div>
        </div>

        <!-- 右侧：场景介绍 -->
        <div class="w-1/2 flex-shrink-0 hidden lg:flex flex-col">
          <div
            class="bg-n-alpha-3 backdrop-blur-[100px] rounded-xl p-5 w-full flex flex-col flex-1 h-full"
          >
            <!-- 当前已关联的知识库（仅 crawlWechat 场景显示） -->
            <div
              v-if="actionType === 'crawlWechat' && displayKnowledgeBase"
              class="mb-4 w-full p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2 text-left"
            >
              <!-- 提示文字 -->
              <span class="text-xs text-n-slate-11 block mb-3">
                {{
                  t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.MANUAL_ASSIGN_HINT')
                }}
              </span>

              <!-- 知识库信息 -->
              <div class="flex items-start justify-between gap-3">
                <!-- Logo -->
                <div
                  class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
                  :class="
                    displayKnowledgeBase.logo_url ||
                    displayKnowledgeBase.logoUrl
                      ? ''
                      : 'bg-n-brand/10'
                  "
                >
                  <img
                    v-if="
                      displayKnowledgeBase.logo_url ||
                      displayKnowledgeBase.logoUrl
                    "
                    :src="
                      displayKnowledgeBase.logo_url ||
                      displayKnowledgeBase.logoUrl
                    "
                    :alt="displayKnowledgeBase.name"
                    class="w-full h-full object-cover"
                  />
                  <span v-else class="text-base font-semibold text-n-brand">
                    {{
                      displayKnowledgeBase.name?.charAt(0)?.toUpperCase() || '?'
                    }}
                  </span>
                </div>

                <!-- 名称和描述 -->
                <div class="flex flex-col min-w-0 flex-1">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ displayKnowledgeBase.name }}
                  </span>
                  <span class="text-sm text-n-slate-11 mt-1 truncate">
                    {{
                      displayKnowledgeBase.description ||
                      t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION')
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 图片展示 -->
            <div class="flex-shrink-0 bg-gray-800 w-full h-[10rem] rounded-lg">
              <template v-if="!imageError">
                <img
                  :src="currentScene.popoverImageDark"
                  alt=""
                  draggable="false"
                  loading="lazy"
                  class="w-full h-full object-contain hidden dark:block"
                  @error="handleImageError"
                />
                <img
                  :src="currentScene.popoverImageLight"
                  alt=""
                  draggable="false"
                  loading="lazy"
                  class="w-full h-full object-contain block dark:hidden"
                  @error="handleImageError"
                />
              </template>
              <!-- 图片加载失败时显示图标 -->
              <div
                v-else
                class="w-full h-full flex items-center justify-center"
                :class="currentScene.bgColor"
              >
                <Icon
                  :icon="currentScene.icon"
                  class="size-7"
                  :style="{ color: currentScene.color }"
                />
              </div>
            </div>

            <!-- 描述文字 -->
            <p
              v-if="currentScene.tip"
              class="text-n-slate-12 text-start text-sm mb-0 whitespace-pre-line mt-4"
            >
              {{ currentScene.tip }}
            </p>

            <!-- 底部引导语 -->
            <p
              v-if="currentScene.footerTip"
              class="text-n-slate-12 text-start text-sm mb-0 whitespace-pre-line mt-auto pt-4"
            >
              {{ currentScene.footerTip }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
